import { toast } from "react-toastify";

import { useConfigurationStore } from "@stores/configuration";
import axios from "@utilities/axios";
import { type TFunction } from "i18next";

export interface OnboardingLicense {
  id: string;
  name: string;
  cli: string;
  email: string;
  social_accounts: string[];
}

/** Sent by pts when it corrected a typo in the email's domain (gmail.con → gmail.com). */
export interface EmailCorrection {
  from: string;
  to: string;
  message: string;
}

export interface OnboardingResponse {
  email_corrected?: EmailCorrection;
  status: string;
  licenses?: OnboardingLicense[];
  social_accounts?: string[];
  username?: string;
  license_name?: string;
  email?: string;
  hashed_password?: string;
  message?: string;
  transaction_id?: string;
}

export interface ActivationStatusResponse {
  message?: string;
  username?: string;
  hashed_password?: string;
}

/** UI locale for guest emails (pts spec §21): gatsby-plugin-react-i18next sets <html lang>; fall back to the browser language. */
const currentLanguage = (): string => {
  if (typeof document !== "undefined" && document.documentElement.lang)
    return document.documentElement.lang.split("-")[0];
  if (typeof navigator !== "undefined" && navigator.language)
    return navigator.language.split("-")[0];
  return "en";
};

/**
 * Thrown before any request is made when an OTP send is attempted without an
 * email. With a `license_id` the backend skips its email ↔ license check when
 * no email is sent, and pts P5 will reject it outright — so an empty email is
 * a caller bug (typically a stale React closure), never a valid request.
 */
export class MissingOnboardingEmailError extends Error {
  constructor(action: string) {
    super(`${action}: email is required`);
    this.name = "MissingOnboardingEmailError";
  }
}

const assertEmail = (email: string, action: string): void => {
  if (email.trim() !== "") return;
  const error = new MissingOnboardingEmailError(action);
  // Callers catch and toast a generic message; keep the real cause visible.
  console.error("[onboarding]", error);
  throw error;
};

/**
 * The onboarding rate limit (pts P4, send and verify). OnboardingSelfcareApi
 * wraps every exception as BaseException(209, message), so it normally arrives
 * as an HTTP 209 — a 2xx that axios RESOLVES — with body
 * {"code":209,"message":"Too many attempts. Please try again later."}. Match
 * the message, whatever the code; a genuine HTTP 429 is handled too.
 */
const RATE_LIMIT_MESSAGE = /too many attempts/i;
export const isOnboardingRateLimited = (data: any): boolean =>
  typeof data?.message === "string" && RATE_LIMIT_MESSAGE.test(data.message);

/**
 * User-facing message for an onboarding response that isn't a known status
 * (the `default:` branches). Rate-limited → localized "try again later".
 */
export const getOnboardingResponseMessage = (
  response: OnboardingResponse,
  t: TFunction,
): string => {
  if (isOnboardingRateLimited(response)) return t("too_many_attempts");
  return response.message ?? "";
};

/**
 * User-facing message for a failed (rejected) onboarding call. Rate-limited →
 * fixed "try again later" message; callers never retry automatically. A
 * missing-email guard failure is a client bug, so the user gets the generic
 * error rather than the developer message.
 */
export const getOnboardingErrorMessage = (error: any, t: TFunction): string => {
  if (
    error?.response?.status === 429 ||
    isOnboardingRateLimited(error?.response?.data)
  ) {
    return t("too_many_attempts");
  }
  if (error instanceof MissingOnboardingEmailError) {
    return t("unexpected_error_occurred");
  }
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    ""
  );
};

/**
 * The address pts corrected the email to, or null. my_account_details also
 * sends the field on error answers (blocked domain, mail not sent), where the
 * correction was not applied or the customer gets the error instead.
 */
export const getCorrectedEmail = (data: any): string | null => {
  if (data?.message_status === "error") return null;
  return data?.email_corrected?.to ?? null;
};

export const toastEmailCorrected = (t: TFunction, email: string): void => {
  // toastId: a checkout save sends the same address for shipping and billing.
  toast.info(t("email_corrected_notice", { email }), {
    autoClose: 8000,
    toastId: `email-corrected-${email}`,
  });
};

export const sendOtpToEmail = async (
  email: string,
  licenseId?: string,
): Promise<OnboardingResponse> => {
  assertEmail(email, "sendOtpToEmail");
  const formData = new FormData();
  formData.append("action", "send_otp_to_email");
  formData.append("language", currentLanguage());
  formData.append("email", email);
  formData.append("app", process.env.GATSBY_AFFILIATE_APP_OWNER ?? "");
  if (licenseId) {
    formData.append("license_id", licenseId);
  }
  const response = await axios.post("/onboarding/", formData);
  return response.data as OnboardingResponse;
};

export const resendOtp = async (email: string): Promise<OnboardingResponse> => {
  assertEmail(email, "resendOtp");
  const formData = new FormData();
  formData.append("action", "resend_otp");
  formData.append("language", currentLanguage());
  formData.append("email", email);
  formData.append("from", "esim");
  formData.append("app", process.env.GATSBY_AFFILIATE_APP_OWNER ?? "");
  const response = await axios.post("/onboarding/", formData);
  return response.data as OnboardingResponse;
};

export const verifyEmailOtp = async (
  email: string,
  otp: string,
): Promise<OnboardingResponse> => {
  const formData = new FormData();
  formData.append("action", "verify_email_with_otp");
  formData.append("email", email);
  formData.append("app", process.env.GATSBY_AFFILIATE_APP_OWNER ?? "");
  formData.append("otp", otp);
  const response = await axios.post("/onboarding/", formData);
  return response.data as OnboardingResponse;
};

export const checkActivationStatus = async (
  transactionId: string,
  email: string,
): Promise<ActivationStatusResponse> => {
  const voucherCode = useConfigurationStore.getState().affiliate.toUpperCase();
  const formData = new FormData();
  formData.append("is_app_registration", "1");
  formData.append("voucher-code", voucherCode);
  formData.append("transaction-id", transactionId);
  formData.append("contact_email", email);
  const response = await axios.post("/sim_card_activation_status/", formData);
  return response.data as ActivationStatusResponse;
};

export const registerUser = async (
  email: string,
): Promise<OnboardingResponse> => {
  let hm = "";
  let nn = "";
  try {
    if (window?.generateHmacAndNonce != null) {
      const result = await window.generateHmacAndNonce();
      hm = result.hmacToken;
      nn = result.nonce;
    }
  } catch (error) {
    console.error("[onboarding] Error generating HMAC and Nonce:", error);
  }

  const voucherCode = useConfigurationStore.getState().affiliate.toUpperCase();

  const formData = new FormData();
  formData.append("action", "register");
  formData.append("language", currentLanguage());
  formData.append("email", email);
  formData.append("app", process.env.GATSBY_AFFILIATE_APP_OWNER ?? "");
  formData.append("is_app_registration", "1");
  formData.append("from", "esim");
  formData.append("source", "landing_page");
  formData.append("h", hm);
  formData.append("n", nn);
  formData.append("voucher-code", voucherCode);

  const response = await axios.post("/onboarding/", formData);
  return response.data as OnboardingResponse;
};

export interface AccountDetailResponse {
  message_status?: string;
  message?: string;
  // Returned by action=change-password: the NEW password's bcrypt hash, used
  // to refresh the session via loginWithLicenseToken without going through
  // /autoprovision/ (whose cli+app lookup 406s for accounts under
  // affiliates with empty app_owner).
  hashed_password?: string;
}

export const updateFullName = async (
  fullName: string,
): Promise<AccountDetailResponse> => {
  const formData = new FormData();
  formData.append("action", "edit");
  formData.append("full_name", fullName);
  const response = await axios.post("/my_account_details/", formData);
  return response.data as AccountDetailResponse;
};

export const changePassword = async (
  password: string,
  confirmPassword: string,
): Promise<AccountDetailResponse> => {
  const formData = new FormData();
  formData.append("action", "change-password");
  formData.append("new-password-1", password);
  formData.append("new-password-2", confirmPassword);
  const response = await axios.post("/my_account_details/", formData);
  return response.data as AccountDetailResponse;
};
